// ============================================================
// Módulo compartido para generación de PDFs de Historia Clínica
// Usado por: Dashboard.js (doctora) y PacientePortal.js (pacientes)
// ============================================================

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Estilos CSS compartidos para ambos PDFs
const pdfStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Montserrat','Poppins',sans-serif; font-size:9.5pt; color:#2D4F4A; padding:1.8cm 2cm; background:#fff; line-height:1.4; }

  .hdr { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:1.6rem; padding-bottom:1rem; position:relative; }
  .hdr::after { content:''; position:absolute; bottom:0; left:0; right:0; height:2px; background:linear-gradient(90deg,transparent 0%,#C4956A 20%,#C4956A 80%,transparent 100%); }
  .hdr img { height:55px; width:auto; margin-right:1.2rem; }
  .brand { font-family:'Lora',serif; font-size:18pt; font-weight:700; color:#2D4F4A; letter-spacing:-0.8px; }
  .sub { font-size:8pt; color:#C4956A; margin-top:0.25rem; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; }
  .hdr-right { text-align:right; font-size:8pt; }
  .hdr-right > div { margin:0.15rem 0; color:#555; line-height:1.4; font-weight:500; }
  .hdr-right .paciente { font-weight:700; color:#2D4F4A; font-size:9pt; }

  h2 { font-family:'Lora',serif; font-size:11.5pt; color:#2D4F4A; margin:1.2rem 0 0.6rem; font-weight:700; letter-spacing:-0.5px; }
  h2::before { content:''; display:inline-block; width:4px; height:14pt; background:linear-gradient(180deg,#C4956A 0%,#F5B8A0 100%); margin-right:0.5rem; vertical-align:-1pt; }

  .grid { display:grid; grid-template-columns:1fr 1fr; gap:0.5rem; margin-bottom:0.8rem; }
  .grid-3 { grid-template-columns:1fr 1fr 1fr; }
  .campo { background:linear-gradient(135deg,#FAF7F4 0%,#FDFBF8 100%); border:1.5px solid #E8D5C4; border-radius:8px; padding:0.5rem 0.75rem; box-shadow:0 2px 4px rgba(45,79,74,0.05),inset 0 1px 0 rgba(255,255,255,0.5); }
  .cl { font-size:7pt; font-weight:700; color:#A08876; text-transform:uppercase; letter-spacing:0.6px; margin-bottom:0.2rem; }
  .cv { font-size:9.5pt; color:#2D4F4A; font-weight:600; line-height:1.4; }

  .sesion { background:linear-gradient(135deg,#FAF7F4 0%,#F8F6F3 100%); border-left:4px solid #C4956A; padding:0.7rem 0.9rem; margin-bottom:0.8rem; border-radius:0 8px 8px 0; box-shadow:0 3px 8px rgba(45,79,74,0.08); }
  .sesion-titulo { font-family:'Lora',serif; font-weight:700; color:#2D4F4A; font-size:11pt; margin-bottom:0.25rem; letter-spacing:-0.3px; }
  .sesion-meta { font-size:8pt; color:#999; margin-bottom:0.4rem; font-weight:500; }

  .texto-bloque { background:linear-gradient(135deg,#FDFBF8 0%,#FAF9F7 100%); border-left:3px solid #F5B8A0; padding:0.65rem 0.8rem; border-radius:0 6px 6px 0; margin:0.5rem 0; font-size:9.5pt; line-height:1.6; color:#2D4F4A; box-shadow:0 1px 3px rgba(245,184,160,0.1); }
  .texto-bloque-titulo { font-weight:700; color:#2D4F4A; margin-bottom:0.3rem; text-transform:uppercase; font-size:7.5pt; letter-spacing:0.6px; color:#A08876; }

  .tareas-bloque { background:linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%); border-left:3px solid #C4956A; padding:0.65rem 0.8rem; border-radius:0 6px 6px 0; margin:0.5rem 0; font-size:9.5pt; line-height:1.6; color:#2D4F4A; box-shadow:0 1px 3px rgba(196,149,106,0.1); }

  .firma-bloque { margin-top:2rem; padding-top:1.2rem; border-top:2px solid #E8D5C4; display:flex; flex-direction:column; align-items:center; gap:0.15rem; text-align:center; }
  .firma-bloque::before { content:''; display:block; width:60%; height:1px; background:linear-gradient(90deg,transparent 0%,#C4956A 50%,transparent 100%); margin-bottom:0.7rem; }
  .firma-img { height:70px; display:block; margin:0 auto 0.5rem; }
  .firma-nombre { font-family:'Lora',serif; font-size:11pt; font-weight:700; color:#2D4F4A; letter-spacing:-0.3px; margin-top:0.8rem; }
  .firma-sub { font-size:7.5pt; color:#999; margin-top:0.25rem; font-weight:500; }
  .firma-info { font-size:7.5pt; color:#777; margin:0.12rem 0; }

  @media print { body { padding:1cm 1.3cm; background:#fff; } }
`;

// Obtener datos del profesional desde API con fallback
export async function getProfesionalData(usuarioId, baseUrl) {
  const fallback = {
    nombre: 'Psicóloga Dani Lopera',
    firma_url: '/uploads/firmas/firma_3_1775836732.png',
    codigo_retus: '05-6200-22',
    tarjeta_profesional: '257050'
  };

  if (!usuarioId) return fallback;

  try {
    const res = await fetch(`${API_BASE}/psicologa-info.php?usuario_id=${usuarioId}`);
    if (!res.ok) return fallback;
    const data = await res.json();
    return data?.nombre ? data : fallback;
  } catch (err) {
    console.error('Error cargando datos de psicóloga:', err);
    return fallback;
  }
}

// Construir bloque de firma
function buildFirmaBlock(profesional, baseUrl) {
  let firmaImg = '<img src="" alt="Firma" class="firma-img"/>';
  if (profesional.firma_url) {
    firmaImg = `<img src="${baseUrl}${profesional.firma_url}" alt="Firma" class="firma-img"/>`;
  }

  let retus = '';
  let tarjeta = '';
  if (profesional.codigo_retus) {
    retus = `<div class="firma-info">RETUS: ${profesional.codigo_retus}</div>`;
  }
  if (profesional.tarjeta_profesional) {
    tarjeta = `<div class="firma-info">Tarjeta profesional: ${profesional.tarjeta_profesional}</div>`;
  }

  return `
    <div class="firma-bloque">
      ${firmaImg}
      <div class="firma-nombre">${profesional.nombre}</div>
      ${tarjeta}${retus}
      <div class="firma-sub">Documento confidencial · uso exclusivo del profesional tratante</div>
    </div>
  `;
}

// Construir bloque de información del paciente
function buildPacienteInfo(paciente, edad) {
  return `
    <h2>Información del Paciente</h2>
    <div class="grid grid-3">
      <div class="campo"><div class="cl">Paciente</div><div class="cv">${paciente.nombre || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Cédula</div><div class="cv">${paciente.cedula || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Edad</div><div class="cv">${edad} años</div></div>
    </div>
    <div class="grid grid-3">
      <div class="campo"><div class="cl">Email</div><div class="cv">${paciente.email || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Teléfono</div><div class="cv">${paciente.telefono || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Ocupación</div><div class="cv">${paciente.ocupacion || 'N/A'}</div></div>
    </div>
    <div class="grid grid-3">
      <div class="campo"><div class="cl">Género</div><div class="cv">${paciente.genero ? paciente.genero.replace('_', ' ') : 'N/A'}</div></div>
      <div class="campo"><div class="cl">Estado Civil</div><div class="cv">${paciente.estado_civil || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Educación</div><div class="cv">${paciente.educacion || 'N/A'}</div></div>
    </div>
    <div class="grid grid-3">
      <div class="campo"><div class="cl">País</div><div class="cv">${paciente.pais || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Departamento</div><div class="cv">${paciente.departamento || 'N/A'}</div></div>
      <div class="campo"><div class="cl">Ciudad</div><div class="cv">${paciente.ciudad || 'N/A'}</div></div>
    </div>
    <div class="grid">
      <div class="campo"><div class="cl">Estado</div><div class="cv">${paciente.estado || 'N/A'}</div></div>
    </div>
    ${paciente.motivo_consulta ? `<div style="margin-bottom:1rem"><div style="font-size:7pt;font-weight:700;color:#A08876;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:0.2rem">Motivo de Consulta</div><div style="font-size:9.5pt;color:#2D4F4A;font-weight:600;line-height:1.4">${paciente.motivo_consulta}</div></div>` : ''}
    ${paciente.diagnostico ? `<div style="margin-bottom:1rem"><div style="font-size:7pt;font-weight:700;color:#A08876;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:0.2rem">Diagnóstico</div><div style="font-size:9.5pt;color:#2D4F4A;font-weight:600;line-height:1.4">${paciente.diagnostico}</div></div>` : ''}
  `;
}

// Construir HTML de una sesión
function buildSesionHtml(sesion, indice) {
  return `
    <div class="sesion">
      <div class="sesion-titulo">Sesión ${indice + 1} • ${sesion.tema}</div>
      <div class="sesion-meta">${new Date(sesion.fecha).toLocaleDateString('es-CO')} • ${sesion.duracion} minutos</div>
      ${sesion.estado_emocional ? `<div class="campo" style="margin-bottom:0.4rem"><div class="cl">Estado Emocional</div><div class="cv">${sesion.estado_emocional.replace('_', ' ')}</div></div>` : ''}
      ${sesion.diagnosticos ? `<div class="campo" style="margin-bottom:0.4rem"><div class="cl">Diagnósticos CIE10</div><div class="cv">${sesion.diagnosticos.split(' | ').filter(d => d && d !== 'dummy_end').join(', ')}</div></div>` : ''}
      ${sesion.tecnicas_utilizadas ? `<div class="campo" style="margin-bottom:0.4rem"><div class="cl">Técnicas Utilizadas</div><div class="cv">${sesion.tecnicas_utilizadas}</div></div>` : ''}
      ${sesion.objetivos_sesion ? `<div class="texto-bloque"><div class="texto-bloque-titulo">Cuadro Clínico de Atención</div>${sesion.objetivos_sesion}</div>` : ''}
      ${sesion.notas ? `<div class="texto-bloque"><div class="texto-bloque-titulo">Resumen de la Atención</div>${sesion.notas}</div>` : ''}
      ${sesion.tareas_asignadas ? `<div class="tareas-bloque"><div class="texto-bloque-titulo">Tareas y Recomendaciones</div>${sesion.tareas_asignadas}</div>` : ''}
      ${sesion.proxima_sesion_recomendada ? `<div class="campo" style="margin-bottom:0.4rem"><div class="cl">Próxima Sesión Recomendada</div><div class="cv">${new Date(sesion.proxima_sesion_recomendada).toLocaleDateString('es-CO')}</div></div>` : ''}
    </div>
  `;
}

// Abrir ventana y lanzar print
async function openPrintWindow(titulo, htmlContent, baseUrl) {
  try {
    // Crear elemento temporal para renderizar
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = 'fixed';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '210mm'; // A4 width
    tempDiv.style.backgroundColor = 'white';
    document.body.appendChild(tempDiv);

    // Convertir HTML a canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    // Crear PDF desde canvas
    const imgWidth = 210; // A4 width en mm
    const pageHeight = 297; // A4 height en mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    const pdf = new jsPDF('p', 'mm', 'A4');
    let position = 0;

    // Agregar páginas si es necesario
    const imgData = canvas.toDataURL('image/png');
    while (heightLeft >= 0) {
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      if (heightLeft > 0) {
        pdf.addPage();
        position = -imgHeight + pageHeight;
      }
    }

    // Descargar PDF
    const nombreArchivo = `${titulo.replace(/[^a-z0-9áéíóúñ]/gi, '_').toLowerCase()}.pdf`;
    pdf.save(nombreArchivo);

    // Limpiar
    document.body.removeChild(tempDiv);
  } catch (err) {
    console.error('Error generando PDF:', err);
    alert('Error al generar PDF. Intenta nuevamente.');
  }
}

// ============================================================
// FUNCIÓN PÚBLICA: Generar PDF de sesión individual
// ============================================================
export async function generarPDFSesionIndividual({ paciente, sesion, indice, profesional, baseUrl }) {
  const edad = paciente.fecha_nacimiento
    ? new Date().getFullYear() - new Date(paciente.fecha_nacimiento).getFullYear()
    : 'N/A';

  const header = `
    <div class="hdr">
      <div style="display:flex;align-items:center;gap:1.2rem;flex:1">
        <img src="${baseUrl}/images/logo-horizontal.png" alt="Logo" style="height:55px;width:auto"/>
        <div>
          <div class="brand">Psicóloga Dani Lopera</div>
          <div class="sub">Sesión ${indice + 1}</div>
          <div style="font-size:8pt;color:#999;margin-top:0.35rem;font-weight:400">${new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
      </div>
      <div class="hdr-right">
        <div class="paciente">${paciente.nombre}</div>
        <div>ID: ${String(paciente.id || '').padStart(4, '0')}</div>
        <div style="color:#C4956A;font-weight:700;margin-top:0.25rem;font-size:8.5pt">● CONFIDENCIAL</div>
      </div>
    </div>
  `;

  const body = `
    ${buildPacienteInfo(paciente, edad)}
    <h2>Sesión Clínica</h2>
    ${buildSesionHtml(sesion, indice)}
  `;

  const firma = buildFirmaBlock(profesional, baseUrl);

  const htmlContent = `${header}${body}${firma}`;
  openPrintWindow(`Historia Clínica - Sesión ${indice + 1}`, htmlContent, baseUrl);
}

// ============================================================
// FUNCIÓN PÚBLICA: Generar PDF de historia clínica completa
// ============================================================
export async function generarPDFHistoriaCompleta({ paciente, sesiones, profesional, baseUrl }) {
  const edad = paciente.fecha_nacimiento
    ? new Date().getFullYear() - new Date(paciente.fecha_nacimiento).getFullYear()
    : 'N/A';

  // Recolectar diagnósticos únicos de todas las sesiones
  const diagnosticosSet = new Set();
  (sesiones || []).forEach(s => {
    if (s.diagnosticos) {
      s.diagnosticos.split(' | ').forEach(d => {
        if (d && d !== 'dummy_end') diagnosticosSet.add(d);
      });
    }
  });
  const diagnosticosGenerales = Array.from(diagnosticosSet).join(', ') || 'No especificados';

  const header = `
    <div class="hdr">
      <div style="display:flex;align-items:center;gap:1.2rem;flex:1">
        <img src="${baseUrl}/images/logo-horizontal.png" alt="Logo" style="height:55px;width:auto"/>
        <div>
          <div class="brand">Psicóloga Dani Lopera</div>
          <div class="sub">Historia Clínica Completa</div>
          <div style="font-size:8pt;color:#999;margin-top:0.35rem;font-weight:400">${new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
      </div>
      <div class="hdr-right">
        <div class="paciente">${paciente.nombre}</div>
        <div>ID: ${String(paciente.id || '').padStart(4, '0')}</div>
        <div style="color:#C4956A;font-weight:700;margin-top:0.25rem;font-size:8.5pt">● CONFIDENCIAL</div>
      </div>
    </div>
  `;

  const sesionesHtml = (sesiones || [])
    .map((s, i) => buildSesionHtml(s, i))
    .join('');

  const body = `
    ${buildPacienteInfo(paciente, edad)}
    <h2>Diagnósticos Generales</h2>
    <div class="campo"><div class="cl">Diagnósticos CIE10</div><div class="cv">${diagnosticosGenerales}</div></div>
    ${sesiones && sesiones.length > 0 ? `
      <h2>Historial de Sesiones</h2>
      ${sesionesHtml}
    ` : '<p style="text-align:center;color:#999;margin:2rem 0">Sin sesiones registradas</p>'}
  `;

  const firma = buildFirmaBlock(profesional, baseUrl);

  const htmlContent = `${header}${body}${firma}`;
  openPrintWindow('Historia Clínica Completa', htmlContent, baseUrl);
}
